import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, ConfirmDialog, Details, Section, Technical, StateView, formatRelative, useAuth, useConfirm, useToast } from "@ozpanel/ui";
import { Field } from "./Form";
import { describeUserAgent } from "./userAgent";
import { DataList, type DataListColumn } from "./DataList";
import { StatusPill } from "./PageHeader";
import { SecretReveal } from "./SecretReveal";

// Your own account's security: changing your password, turning on two-factor, and seeing which
// sessions are signed in as you.
//
// IN kit, not in one console, because it is the same for every role. It existed only in the
// customer console; the mailbox console had its own copy; the ADMIN console had neither — so a
// platform administrator could not see their own sessions or enrol a second factor, which is the
// account on this platform where both matter most.
//
// Nothing here is scoped to a tenant. /auth/password, /auth/2fa/* and /auth/sessions all act on
// the CALLER, so the server decides what they return and no console has to filter anything —
// a client-side scope check would be a permission decision in the wrong process.

interface Session {
  id?: number | string;
  created_at?: string;
  // last_seen_at, not last_seen — the column read a field that does not exist and was blank.
  last_seen_at?: string;
  ip?: string;
  user_agent?: string;
  current?: boolean;
  // The server's name for the device (auth.DeviceName — the one rule), how it signed in, and who issued
  // the phone-setup code it came from.
  device_name?: string;
  origin?: "password" | "pair" | "impersonation" | "passwordless" | "";
  paired_by?: string;
}

// How a device signed in, in words; nothing for a password sign-in (the ordinary case) or an unknown one.
const ORIGIN_WORDS: Record<string, string> = {
  pair: "set up by code",
  impersonation: "an administrator acting as you",
  passwordless: "signed in by the panel",
};

export function PasswordAndTwoFactor({
  // The password this changes is the SIGN-IN (panel) password — /auth/password acts on the caller.
  // Most consoles have only one password so "Change password" is right; the mailbox console has a
  // second, separate MAIL password, so it passes a title and hint that say which one this is. The
  // defaults keep every existing caller (account, admin) unchanged.
  passwordTitle = "Change password",
  passwordHint,
}: { passwordTitle?: string; passwordHint?: React.ReactNode } = {}) {
  const { client, who } = useAuth();
  const { notify } = useToast();
  const confirm = useConfirm();
  const [cur, setCur] = React.useState("");
  const [nw, setNw] = React.useState("");
  const [again, setAgain] = React.useState("");
  // `qr` is a PNG data URI rendered by the server; `otpauth_url` and `otpauth_uri` are BOTH sent
  // because this console read `_url` while the handler sent only `_uri` — a field-shape mismatch
  // the live sweep cannot catch, since it never issues writes and this is a POST.
  const [tfa, setTfa] = React.useState<{ secret?: string; otpauth_url?: string; otpauth_uri?: string; qr?: string } | null>(null);
  const [code, setCode] = React.useState("");
  const [curPw, setCurPw] = React.useState("");
  const [totp, setTotp] = React.useState("");
  const [codes, setCodes] = React.useState<string[] | null>(null);
  const [busy, setBusy] = React.useState(false);
  // What the session said at sign-in, updated by what happens here. null = the server did not say,
  // and then both the set-up and the manage controls are offered, as before.
  const [enabled, setEnabled] = React.useState<boolean | null>(who?.two_factor ?? null);

  const change = useMutation({
    // v1 posts {old_password,new_password} here — NOT {current_password}, which is what
    // /mail/me/password takes. Two endpoints, two field names; sending the wrong one is a 400
    // that reads like a wrong password.
    mutationFn: () =>
      client.apiFetch("/auth/password", {
        method: "POST",
        body: JSON.stringify({ old_password: cur, new_password: nw }),
      }),
    onSuccess: () => {
      setCur("");
      setNw("");
      setAgain("");
      notify({ kind: "success", title: "Password changed" });
    },
    onError: (e: unknown) =>
      notify({ kind: "error", title: "Could not change your password", body: e instanceof Error ? e.message : String(e) }),
  });

  const mismatch = again !== "" && nw !== again;

  const turnOff = async () => {
    setBusy(true);
    try {
      await client.apiFetch<unknown>("/auth/2fa", {
        method: "DELETE",
        body: JSON.stringify({ password: curPw, totp: totp.trim() }),
      });
      setCurPw("");
      setTotp("");
      setEnabled(false);
      notify({ kind: "success", title: "Two-factor is off — your recovery codes are destroyed with it" });
    } catch (e) {
      notify({ kind: "error", title: "Could not turn it off", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  // TURNING IT OFF, and NEW RECOVERY CODES. Both endpoints existed and nothing called them, so the
  // panel could switch two-factor ON and then offer no way back and no codes — a person who loses
  // their authenticator then has no route in. Both require BOTH factors, because proving you are
  // the owner matters most at the moment a protection is removed or a permanent bypass is minted.
  const manage = (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Your sign-in password" type="password" autoComplete="current-password" value={curPw} onChange={setCurPw} />
        <Field label="Code from your authenticator" mono inputMode="numeric" autoComplete="one-time-code" value={totp} onChange={setTotp} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={!curPw || !totp.trim() || busy}
          onClick={async () => {
            setBusy(true);
            try {
              const r = await client.apiFetch<{ recovery_codes?: string[] | null }>(
                "/auth/2fa/recovery-codes",
                { method: "POST", body: JSON.stringify({ password: curPw, totp: totp.trim() }) },
              );
              setCodes(r?.recovery_codes ?? []);
              setCurPw("");
              setTotp("");
              notify({ kind: "success", title: "New recovery codes — the old set no longer works" });
            } catch (e) {
              notify({ kind: "error", title: "Could not regenerate", body: e instanceof Error ? e.message : String(e) });
            } finally {
              setBusy(false);
            }
          }}
        >
          New recovery codes
        </Button>
        <Button
          size="sm"
          variant="danger"
          disabled={!curPw || !totp.trim() || busy}
          onClick={() =>
            confirm.ask(
              {
                title: "Turn off two-factor?",
                body: "Signing in will need only your password, and your recovery codes stop working.",
                confirmLabel: "Turn off",
              },
              turnOff,
            )
          }
        >
          Turn off
        </Button>
      </div>
      {codes && codes.length > 0 && (
        <div className="rounded-lg border border-warn/40 bg-warn/5 p-3">
          <div className="text-[12.5px] text-warn">
            Save these now. They are shown once, each works once, and the previous set no longer works.
          </div>
          <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-[13px] select-all">
            {codes.map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {confirm.dialog}
      <Section title={passwordTitle}>
        <div className="space-y-3">
          {passwordHint && <div className="text-[13px] leading-relaxed text-muted">{passwordHint}</div>}
          <Field label="Current password" type="password" autoComplete="current-password" value={cur} onChange={setCur} />
          <Field label="New password" type="password" autoComplete="new-password" value={nw} onChange={setNw} />
          <Field label="New password again" type="password" autoComplete="new-password" value={again} onChange={setAgain} />
          {mismatch && <div className="text-[12.5px] text-crit">The two new passwords do not match.</div>}
          <Button variant="accent" onClick={() => change.mutate()} disabled={change.isPending || !cur || !nw || mismatch}>
            {change.isPending ? "Changing…" : "Change password"}
          </Button>
        </div>
      </Section>

      <Section
        title="Two-factor sign-in"
        actions={
          enabled === true ? <StatusPill tone="ok">on</StatusPill> : enabled === false ? <StatusPill tone="warn">off</StatusPill> : undefined
        }
      >
        <div className="space-y-3">
          <p className="text-[13px] leading-relaxed text-muted">
            {enabled === true
              ? "Signing in asks for a code from your authenticator app as well as your password."
              : "Ask for a code from an authenticator app as well as your password."}
          </p>
          {enabled !== true && !tfa && (
            <Button
              variant="accent"
              onClick={async () => {
                try {
                  setTfa(
                    await client.apiFetch<{ secret?: string; otpauth_url?: string; otpauth_uri?: string; qr?: string }>(
                      "/auth/2fa/enroll",
                      { method: "POST" },
                    ),
                  );
                } catch (e) {
                  notify({ kind: "error", title: "Could not start", body: e instanceof Error ? e.message : String(e) });
                }
              }}
            >
              Set up two-factor
            </Button>
          )}
          {tfa && (
            <div className="space-y-3 rounded-lg border border-line bg-bg2 p-3">
              {/* SAME-DEVICE ENROLMENT FIRST on a phone — you cannot scan your own screen. Tapping the
                  otpauth:// link opens the authenticator with the secret filled in. Nothing is
                  fetched: the URI is a data container, and rel="noreferrer" keeps it from being
                  handed to any other origin. The QR (a data URI from the server, never a
                  third-party QR service, since the URI holds the secret) is for a second device. */}
              {tfa.otpauth_url && (
                <Button asChild variant="accent">
                  <a href={tfa.otpauth_url} rel="noreferrer">
                    Open in your authenticator app
                  </a>
                </Button>
              )}
              {tfa.qr && (
                <div>
                  <div className="text-[12.5px] text-muted">Or scan this from another device:</div>
                  <img src={tfa.qr} alt="Two-factor setup QR code" width={200} height={200} className="mt-2 rounded bg-white p-2" />
                </div>
              )}
              {tfa.secret && (
                <Details summary="Enter the key by hand instead">
                  <div className="break-all rounded border border-line bg-surface px-2 py-1 font-mono text-[13px] select-all">{tfa.secret}</div>
                </Details>
              )}
              <Field label="Code the app shows" mono inputMode="numeric" autoComplete="one-time-code" value={code} onChange={setCode} />
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="accent"
                  disabled={!code.trim()}
                  onClick={async () => {
                    try {
                      await client.apiFetch("/auth/2fa/confirm", { method: "POST", body: JSON.stringify({ code: code.trim() }) });
                      setTfa(null);
                      setCode("");
                      setEnabled(true);
                      notify({ kind: "success", title: "Two-factor is on" });
                    } catch (e) {
                      notify({ kind: "error", title: "That code was not accepted", body: e instanceof Error ? e.message : String(e) });
                    }
                  }}
                >
                  Confirm
                </Button>
                <Button variant="ghost" onClick={() => setTfa(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {enabled !== false && (
            <Details summary={enabled === true ? "Recovery codes or turn it off" : "Already using two-factor? Recovery codes or turn it off"}>
              <p>Both need your sign-in password and a current code.</p>
              {manage}
            </Details>
          )}
        </div>
      </Section>
    </>
  );
}

/**
 * SessionsPanel — every session signed in as you, and a way to end one.
 *
 * Seeing the list is only half of it: a session you cannot end is a report about a credential
 * you can do nothing about, and the one worth finding here is the one you do not recognise.
 */
const SESSIONS_SHOWN = 5;

/**
 * The devices signed in to a login. By default the CALLER's own (/auth/sessions); an account console
 * passes `path` to show one of its mailboxes' devices (GET/DELETE …/mailboxes/{localpart}/sessions).
 */
export function SessionsPanel({
  footer,
  path = "/auth/sessions",
  title = "Where you are signed in",
  emptyMessage = "No other sessions.",
}: { footer?: React.ReactNode; path?: string; title?: string; emptyMessage?: string } = {}) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const queryKey = path === "/auth/sessions" ? ["my-sessions"] : ["sessions", path];

  const sessions = useQuery({
    queryKey,
    queryFn: () => client.apiFetch<{ items: Session[] | null; total: number }>(path),
  });

  const revoke = useMutation({
    mutationFn: (s: Session) => client.apiFetch<unknown>(`${path}/${encodeURIComponent(String(s.id))}`, { method: "DELETE" }),
    onSuccess: () => {
      notify({ kind: "success", title: "Device signed out" });
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) => notify({ kind: "error", title: "Could not sign that session out", body: e instanceof Error ? e.message : String(e) }),
  });

  const cols: DataListColumn<Session>[] = [
    {
      key: "user_agent",
      label: "Device",
      phone: "title",
      value: (s) => s.device_name || describeUserAgent(s.user_agent),
      render: (s) => (
        <span className="inline-flex flex-wrap items-center gap-2">
          {s.device_name || describeUserAgent(s.user_agent)}
          {s.current && <StatusPill tone="accent">this device</StatusPill>}
          {s.origin && ORIGIN_WORDS[s.origin] && (
            <span className="text-[12px] text-muted">
              {ORIGIN_WORDS[s.origin]}
              {s.origin === "pair" && s.paired_by ? ` · by ${s.paired_by}` : ""}
            </span>
          )}
          <Technical>
            <span className="block w-full font-mono text-[11px] break-all text-faint">{s.user_agent}</span>
          </Technical>
        </span>
      ),
    },
    { key: "ip", label: "From", sortable: true, phone: "sub", value: (s) => s.ip || "", render: (s) => <span className="font-mono text-[12.5px]">{s.ip || "—"}</span> },
    { key: "last_seen", label: "Last active", sortable: true, phone: "sub", value: (s) => s.last_seen_at || "", render: (s) => (s.last_seen_at ? formatRelative(s.last_seen_at) : "—") },
  ];

  // This device and the most recently active first; the rest on request. 32 identical cards stood
  // between the reader and "Sign out everywhere" (measured 2026-11-02).
  const [showAll, setShowAll] = React.useState(false);
  const all = React.useMemo(
    () =>
      [...(sessions.data?.items ?? [])].sort(
        (a, b) => Number(!!b.current) - Number(!!a.current) || (b.last_seen_at ?? "").localeCompare(a.last_seen_at ?? ""),
      ),
    [sessions.data],
  );
  const shown = showAll ? all : all.slice(0, SESSIONS_SHOWN);

  return (
    <Section title={title} hint={all.length > 0 ? `${all.length}` : undefined} bodyClassName="p-0">
      <StateView loading={sessions.isLoading} error={sessions.error} errorContext="the signed-in devices">
        <div className="p-4">
          <DataList
            items={shown}
            columns={cols}
            phoneDensity="compact"
            getRowKey={(s) => String(s.id)}
            emptyMessage={emptyMessage}
            // Ending a session is not destructive — the person signs in again — so it is a plain
            // action, not a red one. Ending THIS one is named for what it does: it logs you out.
            rowActions={(s) => (
              <Button size="sm" variant="ghost" disabled={revoke.isPending} onClick={() => revoke.mutate(s)}>
                {s.current ? "Sign out here" : "Sign it out"}
              </Button>
            )}
          />
          {all.length > SESSIONS_SHOWN && (
            <Button size="sm" variant="ghost" className="mt-2" onClick={() => setShowAll((v) => !v)}>
              {showAll ? "Show fewer" : `Show all ${all.length}`}
            </Button>
          )}
        </div>
      </StateView>
      {footer && <div className="border-t border-line px-4 py-3">{footer}</div>}
    </Section>
  );
}

interface PrefRow {
  class: string;
  panel: boolean;
  email: boolean;
  push: boolean;
  default?: boolean;
}

/**
 * NotificationPreferences — which channels each class of event reaches you on.
 *
 * GET/PUT /me/notifications/settings have existed and were called by nothing, so the platform
 * could raise an event, decide it was worth telling somebody about, and offer no way to say
 * where. The bell and the push subscription were both configurable; the CHOICE between them was
 * not.
 *
 * The server's defaults are deliberate and are shown rather than hidden: panel on, push off,
 * because a feed nobody asked for is ignorable and a push nobody asked for is an intrusion —
 * and a browser permission, once denied, cannot be asked for again by this platform.
 */
// A notification class as a person names it. The raw id ("mail") was shown as the label.
const CLASS_LABELS: Record<string, string> = {
  "*": "Everything else",
  mail: "Mail",
  appserver: "Apps",
  backup: "Backups",
  backups: "Backups",
  security: "Security",
  dns: "Domains and DNS",
  certs: "Certificates",
};
function classLabel(c: string): string {
  return CLASS_LABELS[c] ?? c.replace(/[_-]+/g, " ").replace(/^./, (x) => x.toUpperCase());
}

export function NotificationPreferences() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["me-notify-prefs"],
    queryFn: () => client.apiFetch<{ items: PrefRow[] | null; note?: string }>("/me/notifications/settings"),
    retry: false,
  });

  const save = useMutation({
    mutationFn: (row: PrefRow) =>
      client.apiFetch<unknown>("/me/notifications/settings", {
        method: "PUT",
        body: JSON.stringify({ class: row.class, panel: row.panel, email: row.email, push: row.push }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["me-notify-prefs"] }),
    onError: (e) =>
      notify({ kind: "error", title: "Could not save that preference", body: e instanceof Error ? e.message : String(e) }),
  });

  const rows = q.data?.items ?? [];

  return (
    <Section title="How you are told">
      <StateView loading={q.isLoading} error={q.error} errorContext="your notification preferences">
        <div className="space-y-3">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11.5px] text-muted">
                <th className="pb-2 font-medium">Event</th>
                <th className="pb-2 font-medium">Panel</th>
                <th className="pb-2 font-medium">Email</th>
                <th className="pb-2 font-medium">Push</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((r) => (
                <tr key={r.class}>
                  <td className="py-2">
                    {/* "*" is the fallback the server returns when nothing specific is set. Named
                        as such, because a row labelled "*" reads as a bug. */}
                    {classLabel(r.class)}
                    {r.default && <span className="ml-2 text-[11px] text-muted">default</span>}
                  </td>
                  {(["panel", "email", "push"] as const).map((ch) => (
                    <td key={ch} className="py-2">
                      <input
                        type="checkbox"
                        checked={r[ch]}
                        disabled={save.isPending}
                        onChange={(e) => save.mutate({ ...r, [ch]: e.target.checked })}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {q.data?.note && <p className="max-w-2xl text-[11.5px] leading-relaxed text-muted">{q.data.note}</p>}
        </div>
      </StateView>
    </Section>
  );
}

type ApiToken = {
  id: number;
  name: string;
  scopes?: string[] | null;
  allow_dangerous?: boolean;
  expires_at?: string | null;
  last_used_at?: string | null;
  created_at?: string;
};

/**
 * ApiTokensPanel — the caller's OWN API tokens, with what each has actually been used for.
 *
 * Scoped to the caller because that is what the API does: CreateToken binds to p.UserID, so a
 * super_admin mints for themselves and never for another principal. A screen implying otherwise
 * would promise a power that does not exist. The platform-wide view — every key on the NODE — is a
 * different question needing an endpoint nobody has written.
 *
 * `last_used_at` says a token is live; it does not say what it has been DOING, which is the question
 * that decides whether to revoke one. /auth/tokens/{id}/calls answers that from the audit log, and
 * the history OUTLIVES the token: tokens are hard-deleted, so this is the only trail a revoked
 * credential leaves.
 *
 * The value is shown ONCE, on creation. There is no way to retrieve it afterwards and the panel says
 * so before the dialog closes, because the alternative is a user who assumes they can come back.
 */
export function ApiTokensPanel() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [created, setCreated] = React.useState<string | null>(null);
  const [showCalls, setShowCalls] = React.useState<number | null>(null);

  const tokens = useQuery({
    queryKey: ["my-api-tokens"],
    queryFn: () => client.apiFetch<{ items: ApiToken[] | null; total: number }>("/auth/tokens"),
  });
  const calls = useQuery({
    queryKey: ["my-api-token-calls", showCalls],
    enabled: showCalls != null,
    queryFn: () =>
      client.apiFetch<{
        name: string;
        count: number;
        note?: string;
        calls: { module: string; action: string; ok: boolean; ip: string; node: string; at: string; ambiguous: boolean }[];
      }>(`/auth/tokens/${showCalls}/calls?limit=50`),
  });
  const revoke = useMutation({
    mutationFn: (t: ApiToken) => client.apiFetch<unknown>(`/auth/tokens/${t.id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-api-tokens"] }),
  });

  const items = tokens.data?.items ?? [];
  return (
    <Section
      title="API tokens"
      actions={
        <button className="btn btn-sm" onClick={() => setOpen(true)}>
          New token
        </button>
      }
    >
      <div className="px-1 pb-2 text-[12px] leading-relaxed text-muted">
        Tokens act as you, with the scopes you give them. Revoking one deletes it immediately and
        permanently — what it was used for stays in the audit trail, which is the only record a
        deleted token leaves.
      </div>
      {items.length === 0 && <div className="px-1 py-3 text-[13px] text-muted">No API tokens.</div>}
      <div className="space-y-2">
        {items.map((t) => (
          <div key={t.id} className="rounded border border-line px-3 py-2">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-[13px]">
                  <span className="font-medium">{t.name}</span>
                  {t.allow_dangerous && <StatusPill tone="warn">dangerous</StatusPill>}
                  {t.expires_at == null && <StatusPill tone="warn">never expires</StatusPill>}
                </div>
                <div className="text-[11.5px] text-muted">
                  {(t.scopes ?? []).join(", ") || "no scopes"}
                  {" · "}
                  {t.last_used_at ? `last used ${t.last_used_at}` : "never used"}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button className="btn btn-sm" onClick={() => setShowCalls(showCalls === t.id ? null : t.id)}>
                  {showCalls === t.id ? "Hide" : "History"}
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => revoke.mutate(t)}>
                  Revoke
                </button>
              </div>
            </div>
            {showCalls === t.id && (
              <div className="mt-2 border-t border-line pt-2">
                {calls.isLoading && <div className="text-[12px] text-muted">Loading…</div>}
                {calls.data?.note && (
                  <div className="mb-1 text-[11.5px] leading-relaxed text-warn">{calls.data.note}</div>
                )}
                {(calls.data?.calls ?? []).length === 0 && !calls.isLoading && (
                  <div className="text-[12px] text-muted">This token has not been used.</div>
                )}
                {(calls.data?.calls ?? []).map((c, i) => (
                  <div key={i} className="flex gap-2 text-[11.5px] leading-relaxed">
                    <span className="text-muted">{c.at.slice(0, 19).replace("T", " ")}</span>
                    <span className={c.ok ? "text-ok" : "text-bad"}>{c.ok ? "ok" : "failed"}</span>
                    <span className="truncate">{c.action}</span>
                    <span className="text-muted">{c.node}</span>
                    {/* Marked, not hidden: an operator investigating a credential needs everything
                        that might be it, but must never read a maybe as a certainty. */}
                    {c.ambiguous && <span className="text-warn">name-matched only</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {open && (
        <NewTokenDialog
          onClose={() => setOpen(false)}
          onCreated={(v) => {
            setCreated(v);
            setOpen(false);
            qc.invalidateQueries({ queryKey: ["my-api-tokens"] });
          }}
        />
      )}
      {created && <SecretReveal title="Copy this token now" secret={created} onClose={() => setCreated(null)} />}
    </Section>
  );
}

function NewTokenDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (v: string) => void }) {
  const { client } = useAuth();
  const [name, setName] = React.useState("");
  const [scopes, setScopes] = React.useState("*:admin");
  const [dangerous, setDangerous] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      client.apiFetch<{ token: string }>("/auth/tokens", {
        method: "POST",
        body: JSON.stringify({
          name,
          scopes: scopes.split(",").map((s) => s.trim()).filter(Boolean),
          allow_dangerous: dangerous,
        }),
      }),
    onSuccess: (r: any) => onCreated(r?.data?.token ?? r?.token ?? ""),
    onError: (e: any) => setErr(String(e?.message ?? e)),
  });
  return (
    <ConfirmDialog
      open
      title="New API token"
      body={
        <div className="space-y-2">
          <label className="block text-[12px]">
            Name
            <input className="input mt-1 w-full" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="block text-[12px]">
            Scopes (comma separated)
            <input className="input mt-1 w-full" value={scopes} onChange={(e) => setScopes(e.target.value)} />
          </label>
          <label className="flex items-center gap-2 text-[12px]">
            <input type="checkbox" checked={dangerous} onChange={(e) => setDangerous(e.target.checked)} />
            Allow destructive operations
          </label>
          {/* Named because it is the difference between a token that can read and one that can
              evict a node, and the checkbox alone does not say that. */}
          {dangerous && (
            <div className="text-[11.5px] leading-relaxed text-warn">
              This token will be able to perform destructive actions — evicting a node, rolling out a
              binary, deleting data — without a further confirmation.
            </div>
          )}
          {err && <div className="text-[12px] text-bad">{err}</div>}
        </div>
      }
      confirmLabel="Create"
      confirmDisabled={!name.trim()}
      pending={create.isPending}
      onOpenChange={(o: boolean) => !o && onClose()}
      onConfirm={() => create.mutate()}
    />
  );
}
